import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { Breadcrumbs, Icon, Badge, Button, Skeleton, EmptyState } from '../../components/ui';
import { fetchPujaById } from '../../services/epujaService';

const FALLBACK_IMG = '/images/design/hero-ram-mandir.webp';
const TIER_ORDER = { basic: 0, standard: 1, premium: 2 };

export default function PujaDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [puja, setPuja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    window.scrollTo(0, 0);
    setLoading(true); setError(false);
    fetchPujaById(id)
      .then((data) => {
        if (!alive) return;
        setPuja(data);
        const pkgs = [...(data.packages || [])].sort((a, b) => (TIER_ORDER[a.tier] ?? 0) - (TIER_ORDER[b.tier] ?? 0));
        setSelected(pkgs[0]?.id || null);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="container detail-wrap">
        <div className="detail">
          <div className="detail__main"><Skeleton height={340} radius="var(--r-xl)" /><Skeleton height={40} width="55%" /><Skeleton height={140} /></div>
          <div className="detail__aside"><Skeleton height={260} radius="var(--r-lg)" /></div>
        </div>
      </div>
    );
  }

  if (error || !puja) {
    return (
      <div className="container detail-wrap">
        <EmptyState icon="diya" title={t('Puja not found')} body={t('This puja is no longer listed.')} action={<Button variant="outline" to="/epuja">{t('Back to Puja Booking')}</Button>} />
      </div>
    );
  }

  const packages = [...(puja.packages || [])].sort((a, b) => (TIER_ORDER[a.tier] ?? 0) - (TIER_ORDER[b.tier] ?? 0));
  const chosen = packages.find((p) => p.id === selected) || null;
  const displayPrice = chosen ? Number(chosen.price) : Number(puja.basePrice) || null;
  const image = puja.imageUrl || puja.image || FALLBACK_IMG;

  const book = () => navigate(chosen ? `/epuja/${puja.id}/book?package=${chosen.id}` : `/epuja/${puja.id}/book`);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: puja.name,
    description: puja.detailedDescription || puja.description,
    serviceType: 'Puja / religious ritual',
    areaServed: 'Ayodhya, Uttar Pradesh, India',
    ...(displayPrice ? { offers: { '@type': 'Offer', price: displayPrice, priceCurrency: 'INR' } } : {}),
  };

  return (
    <>
      <SEO title={`${puja.name} — Puja Booking`} description={puja.description} canonicalUrl={`/epuja/${id}`} image={image} structuredData={schema} />

      <div className="container detail-wrap">
        <Breadcrumbs className="detail__crumbs" items={[{ label: t('Home'), to: '/' }, { label: t('Puja Booking'), to: '/epuja' }, { label: puja.name }]} />

        <div className="detail">
          <div className="detail__main">
            <div className="gallery">
              <div className="gallery__main"><img src={image} alt={puja.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} /></div>
            </div>

            <div className="detail__head">
              <div className="detail__tags">
                {puja.category && <Badge variant="soft">{puja.category}</Badge>}
                {puja.liveStreaming && <Badge variant="outline"><Icon name="video" size={13} />{t('Live video')}</Badge>}
                {puja.prasadDelivery && <Badge variant="outline"><Icon name="package" size={13} />{t('Prasad delivery')}</Badge>}
              </div>
              <h1 className="detail__title">{puja.name}</h1>
              {i18n.language === 'en' && puja.nameHindi && <div className="detail__hindi hindi">{puja.nameHindi}</div>}
              <div className="detail__meta">
                {puja.durationMinutes && <span><Icon name="clock" size={15} />{t('{{n}} minutes', { n: puja.durationMinutes })}</span>}
                {puja.temple && <span><Icon name="temple" size={15} />{puja.temple}</span>}
                {puja.deity && <span><Icon name="om" size={15} />{puja.deity}</span>}
                {puja.totalBookings > 0 && <span><Icon name="users" size={15} />{t('{{n}} bookings', { n: puja.totalBookings.toLocaleString('en-IN') })}</span>}
              </div>
            </div>

            <section className="detail__section">
              <h2 className="h-section">{t('About this puja')}</h2>
              <div className="prose"><p>{puja.detailedDescription || puja.description}</p></div>
            </section>

            {puja.benefits?.length > 0 && (
              <section className="detail__section">
                <h2 className="h-section">{t('Benefits')}</h2>
                <ul className="check-list puja-includes">
                  {puja.benefits.map((b) => <li key={b}><Icon name="circle-check" size={18} />{b}</li>)}
                </ul>
              </section>
            )}

            {puja.samagriIncluded?.length > 0 && (
              <section className="detail__section">
                <h2 className="h-section">{t("What's included")}</h2>
                <ul className="check-list puja-includes">
                  {puja.samagriIncluded.map((s) => <li key={s}><Icon name="circle-check" size={18} />{s}</li>)}
                </ul>
              </section>
            )}

            {packages.length > 0 && (
              <section className="detail__section">
                <h2 className="h-section">{t('Choose a package')}</h2>
                <div className="options">
                  {packages.map((pkg) => (
                    <button key={pkg.id} type="button" className={`option${selected === pkg.id ? ' is-selected' : ''}`} onClick={() => setSelected(pkg.id)} style={{ textAlign: 'left', cursor: 'pointer' }}>
                      <div>
                        <div className="option__title">
                          {pkg.name}{pkg.tier === 'standard' && <Badge variant="gold" style={{ marginLeft: 8 }}>{t('Popular')}</Badge>}
                        </div>
                        {pkg.description && <div className="option__desc">{pkg.description}</div>}
                        {pkg.includes?.length > 0 && (
                          <div className="option__meta">
                            {pkg.includes.slice(0, 4).map((inc) => <span key={inc}><Icon name="check" size={13} />{inc}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="option__price">
                        <strong>₹{Number(pkg.price).toLocaleString('en-IN')}</strong>
                        <small>{t('all inclusive')}</small>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="detail__aside">
            <div className="card aside-card">
              <span className="eyebrow">{chosen ? chosen.name : t('Base price')}</span>
              <div className="aside-card__price">
                <strong>{displayPrice ? `₹${displayPrice.toLocaleString('en-IN')}` : t('On request')}</strong>
              </div>
              <div className="aside-card__rows">
                {puja.durationMinutes && <div className="aside-card__row"><span>{t('Duration')}</span><span>{t('{{n}} minutes', { n: puja.durationMinutes })}</span></div>}
                <div className="aside-card__row"><span>{t('Mode')}</span><span>{puja.liveStreaming ? t('Online + at temple') : t('At temple')}</span></div>
                <div className="aside-card__row"><span>{t('Prasad delivery')}</span><span>{puja.prasadDelivery ? t('Included') : t('Not included')}</span></div>
                {puja.temple && <div className="aside-card__row"><span>{t('Temple')}</span><span>{puja.temple}</span></div>}
              </div>
              <Button block onClick={book} icon={<Icon name="arrow-right" size={16} />}>{t('Book this puja')}</Button>
              <p className="aside-card__note">{t('Your sankalp is performed by a verified pandit. You receive a video and a digital certificate.')}</p>
            </div>

            <div className="card aside-card" style={{ background: 'var(--surface-2)' }}>
              <h3 className="h-card">{t('Prefer a specific pandit?')}</h3>
              <p className="aside-card__note">{t('Browse our verified pandits and pick who performs your puja.')}</p>
              <Button size="sm" variant="outline" to="/pandits" icon={<Icon name="arrow-right" size={14} />}>{t('Our pandits')}</Button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
